import {
  Button,
  Control,
  Field,
  Help,
  Input,
  Title,
  Select,
  Icon,
  Card,
  Box,
} from 'rbx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserTag } from '@fortawesome/free-solid-svg-icons';
import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import isUUID from 'is-uuid';
import useUser from '../hooks/useUser';
import { css } from 'emotion';

function UserSearch() {
  const {
    user,
    usersList,
    isLoading,
    getUserById,
    getUserByEmail,
    error,
    userVersion,
    setUserVersion,
    setUser,
    setUsersList,
  } = useUser();
  const [value, setValue] = useState(user ? user.id : '');
  const changeHandler = e => setValue(e.target.value);
  const history = useHistory();

  async function submitHandler(e) {
    e.preventDefault();
    // Check if value is a UUID (user ID) or email
    // and use the corresponding user query function.
    const isUserId = isUUID.anyNonNil(value);
    const getUser = isUserId ? getUserById : getUserByEmail;
    const isBluepill = userVersion === 'bluepill';
    const result = await getUser(value, isBluepill);

    // If result is an array with multiple users, show list (don't navigate)
    if (Array.isArray(result) && result.length > 1) {
      return; // Don't navigate, let the component show the list
    }

    // Single user or single user object: navigate as before
    history.push({
      pathname: '/account',
      search: `?email=${isUserId ? value : encodeURIComponent(value)}`,
    });
  }

  async function handleUserSelect(selectedUser) {
    setUser(selectedUser);
    setUsersList(null);
    // Use user ID instead of email to ensure AccountPage gets the correct single user
    history.push({
      pathname: '/account',
      search: `?email=${encodeURIComponent(selectedUser.id)}`,
    });
  }

  const renderUserList = () => {
    if (!usersList || usersList.length <= 1) return null;

    return (
      <Box
        className={css`
          margin-top: 2em;
        `}
      >
        <Title size={5}>Multiple users found ({usersList.length})</Title>
        <p
          className={css`
            margin-bottom: 1em;
            color: #666;
          `}
        >
          Please select a user to view details:
        </p>
        {usersList.map((u, index) => {
          const name = u.firstName
            ? `${u.firstName}${u.lastName ? ' ' + u.lastName : ''}`
            : 'User';
          const primaryEmail = u.emails?.find(e => e.primary) || {
            email: u.email,
          };

          return (
            <Card
              key={u.id || index}
              className={css`
                margin-bottom: 1em;
                cursor: pointer;
                transition: box-shadow 0.2s;
                &:hover {
                  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
                }
              `}
              onClick={() => handleUserSelect(u)}
            >
              <Card.Content>
                <div
                  className={css`
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                  `}
                >
                  <div>
                    <Title size={5}>{name}</Title>
                    <p
                      className={css`
                        margin: 0.5em 0;
                        color: #666;
                      `}
                    >
                      <strong>Email:</strong> {primaryEmail.email || u.email}
                    </p>
                    <p
                      className={css`
                        margin: 0.25em 0;
                        color: #666;
                      `}
                    >
                      <strong>Domain:</strong> {u.domain || 'N/A'}
                    </p>
                    <p
                      className={css`
                        margin: 0.25em 0;
                        color: #666;
                      `}
                    >
                      <strong>Country:</strong> {u.country || 'N/A'}
                    </p>
                  </div>
                  <div
                    className={css`
                      text-align: right;
                    `}
                  >
                    <span
                      style={{
                        padding: '0.25em 0.75em',
                        borderRadius: '4px',
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        backgroundColor:
                          u.role === 'paid_user'
                            ? '#48c774'
                            : u.role === 'user'
                            ? '#3273dc'
                            : '#7a7a7a',
                        color: 'white',
                        display: 'inline-block',
                        marginBottom: '0.5em',
                      }}
                    >
                      {u.role === 'paid_user' ? 'Paid User' : u.role || 'User'}
                    </span>
                    {u.billingVersion && (
                      <p
                        className={css`
                          margin: 0.25em 0;
                          font-size: 0.875rem;
                          color: #666;
                        `}
                      >
                        Billing v{u.billingVersion}
                      </p>
                    )}
                  </div>
                </div>
              </Card.Content>
            </Card>
          );
        })}
      </Box>
    );
  };

  return (
    <>
      <Title size={5}>Find a user</Title>
      <form
        onSubmit={submitHandler}
        style={{ display: 'flex', justifyContent: 'space-between' }}
      >
        <Field kind="addons" horizontal expanded style={{ width: '50%' }}>
          <Control expanded>
            <Input
              disabled={isLoading}
              placeholder="Search by user ID or email"
              value={value}
              onChange={changeHandler}
              color={error ? 'danger' : ''}
            />
            {error && <Help color="danger">{error}</Help>}
          </Control>
          <Control>
            <Button type="submit" color="primary" disabled={isLoading}>
              Search
            </Button>
          </Control>
        </Field>
        <Control iconLeft>
          <Select.Container>
            <Select
              value={userVersion}
              onChange={e => setUserVersion(e.target.value)}
              disabled={false}
            >
              <Select.Option value="builder">Builder</Select.Option>
              <Select.Option value="bluepill">Bluepill</Select.Option>
            </Select>
          </Select.Container>
          <Icon size="small" align="left">
            <FontAwesomeIcon icon={faUserTag} />
          </Icon>
        </Control>
      </form>
      {renderUserList()}
    </>
  );
}

export default UserSearch;
